'use client';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { FileText, Upload, Trash2 } from 'lucide-react';

type ImportFilesAction = (
  formData: FormData,
  projectId: string,
) => Promise<void>;

interface UploadDocumentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  isUploading: boolean;
  uploadedFiles: File[];
  onFileUpload: (files: FileList) => void;
  onRemoveFile: (fileId: string) => void;
  onImportFiles: (
    action: ImportFilesAction,
    projectId: string,
    getPresignedUrls?: (projectId: string, files: { name: string; size: number; type: string }[]) => Promise<any>,
    confirmUploads?: (projectId: string, files: any[]) => Promise<any>,
  ) => void;
  importFilesAction: ImportFilesAction;
  projectId: string;
  getPresignedUrls?: (projectId: string, files: { name: string; size: number; type: string }[]) => Promise<any>;
  confirmUploads?: (projectId: string, files: any[]) => Promise<any>;
}

export function UploadDocumentsModal({
  isOpen,
  onClose,
  isUploading,
  uploadedFiles,
  onFileUpload,
  onRemoveFile,
  onImportFiles,
  importFilesAction,
  projectId,
  getPresignedUrls,
  confirmUploads,
}: UploadDocumentsModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-gray-900">
            Upload document
          </DialogTitle>
        </DialogHeader>

        {isUploading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Uploading...</span>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Upload Area */}
            <div
              className="relative border-2 border-dashed border-blue-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const files = e.dataTransfer.files;
                if (files.length > 0) {
                  onFileUpload(files);
                }
              }}
            >
              <input
                type="file"
                id="file-upload"
                multiple
                accept=".pdf,.doc,.docx"
                onChange={(e) => {
                  if (e.target.files) {
                    onFileUpload(e.target.files);
                  }
                }}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <div className="flex flex-col items-center space-y-6">
                <Upload className="w-24 h-24 text-blue-500" />
                <div className="text-center">
                  <div className="text-md mb-2">
                    <label
                      htmlFor="file-upload"
                      className="text-blue-600 hover:text-blue-800 cursor-pointer underline"
                    >
                      Click to upload
                    </label>
                    <span className="text-black"> or drag and drop</span>
                  </div>
                  <p className="text-sm text-gray-500">
                    You can upload .pdf, .doc, .docx
                  </p>
                </div>
              </div>
            </div>
            {/* Uploaded Files List */}
            {uploadedFiles.length > 0 && (
              <div className="space-y-3">
                {uploadedFiles.map((file) => (
                  <div
                    key={file.name}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <FileText className="w-8 h-8 text-blue-500" />
                      <div>
                        <p className="font-medium text-gray-900">{file.name}</p>
                        <p className="text-sm text-gray-500">{file.size}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2"></div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemoveFile(file.name)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-row justify-end space-x-2 pt-6">
          <Button variant="outline" onClick={onClose} className="px-6 py-2">
            Cancel
          </Button>
          <Button
            onClick={() => onImportFiles(importFilesAction, projectId, getPresignedUrls, confirmUploads)}
            disabled={uploadedFiles.length === 0 || isUploading}
            className={`px-6 py-2 ${
              uploadedFiles.length > 0 && !isUploading
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isUploading ? 'Importing...' : 'Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
